import {Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import * as markedLib from "marked";

interface TocItem {
    id: string;
    text: string;
    level: number;
}

/**
 * マニュアル表示ページ (active-promotion の ManualComponent と同じ作り)。src/assets/manuals/<file>.md を
 * 取得し marked で HTML にして表示する。ルート data で file / title / pub (ログイン不要か) を受け取る。
 * 同友用・提携先用はログイン画面から読めるよう公開、支部管理者用はログイン後 (app.routing.ts)。
 *
 * <p>見出し (h2/h3) から目次を作ってサイドバーに出し、画像は figure/figcaption にする。
 * スクロールで現在地の目次項目を強調する。
 */
@Component({
    selector: "app-manual",
    templateUrl: "manual.component.html",
    styleUrls: ["manual.component.css"],
    // [innerHTML] で入れた Markdown 由来の要素にもスタイルを効かせるため無効化。
    // セレクタはすべて .manual-* 配下に限定している
    encapsulation: ViewEncapsulation.None,
})
export class ManualComponent implements OnInit, OnDestroy {
    title = "マニュアル";
    subtitle = "";
    html: SafeHtml = "";
    toc: TocItem[] = [];
    activeId = "";
    loading = true;
    error = false;
    isPublic = false;
    tocOpen = false;

    @ViewChild("body") bodyRef: ElementRef<HTMLElement>;
    private observer: IntersectionObserver;

    constructor(private http: HttpClient, private route: ActivatedRoute, private sanitizer: DomSanitizer) {
    }

    ngOnInit(): void {
        const data: any = this.route.snapshot.data || {};
        this.title = data.title || "マニュアル";
        this.isPublic = !!data.pub;
        this.subtitle = this.isPublic ? "ログイン不要" : "ログイン後";
        const file = data.file;
        if (!file) {
            this.error = true;
            this.loading = false;
            return;
        }
        this.http.get("assets/manuals/" + file, {responseType: "text"}).subscribe(
            (md: string) => {
                this.render(md);
                this.loading = false;
                // 描画が反映されてから見出しを監視して、目次の現在地を強調する
                setTimeout(() => this.observeHeadings(), 0);
            },
            () => {
                this.error = true;
                this.loading = false;
            }
        );
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    /** 戻り先。ログイン不要のマニュアルはログイン画面、それ以外は旧画面のホーム */
    backLink(): string[] {
        return this.isPublic ? ["/login"] : ["/legacy/user/home"];
    }

    /** Markdown を HTML にし、見出しに id を付けて目次を作り、画像を figure にする */
    private render(md: string): void {
        const rendered: string = (markedLib as any).parse(md);
        const doc = new DOMParser().parseFromString(rendered, "text/html");

        const toc: TocItem[] = [];
        let n = 0;
        doc.querySelectorAll("h2, h3").forEach((h) => {
            const id = "sec-" + (++n);
            h.setAttribute("id", id);
            toc.push({id: id, text: h.textContent || "", level: h.tagName === "H3" ? 3 : 2});
        });
        this.toc = toc;

        // marked は画像を <p><img></p> で出すので、画像だけの段落は figure で置き換える
        const images = doc.querySelectorAll("img");
        for (let i = 0; i < images.length; i++) {
            const img = images[i];
            const alt = img.getAttribute("alt") || "";
            img.setAttribute("loading", "lazy");
            const fig = doc.createElement("figure");
            fig.className = "manual-figure";
            const p = img.parentElement;
            const soleInParagraph = p && p.tagName === "P" && (p.textContent || "").trim() === "" && p.querySelectorAll("img").length === 1;
            if (soleInParagraph && p.parentNode) {
                p.parentNode.replaceChild(fig, p);
            } else if (img.parentNode) {
                img.parentNode.replaceChild(fig, img);
            } else {
                continue;
            }
            fig.appendChild(img);
            if (alt) {
                const cap = doc.createElement("figcaption");
                cap.textContent = alt;
                fig.appendChild(cap);
            }
        }

        this.html = this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
    }

    /** 目次をクリックしたら該当の見出しへスクロール */
    scrollTo(id: string, ev?: Event): void {
        if (ev) {
            ev.preventDefault();
        }
        this.tocOpen = false;
        const el = this.bodyRef ? this.bodyRef.nativeElement.querySelector("#" + id) as HTMLElement : null;
        if (el) {
            el.scrollIntoView({behavior: "smooth", block: "start"});
            this.activeId = id;
        }
    }

    private observeHeadings(): void {
        if (!this.bodyRef || typeof IntersectionObserver === "undefined") {
            return;
        }
        const heads = this.bodyRef.nativeElement.querySelectorAll("h2, h3");
        if (!heads.length) {
            return;
        }
        this.observer = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (e.isIntersecting) {
                    this.activeId = (e.target as HTMLElement).id;
                }
            }
        }, {rootMargin: "-80px 0px -70% 0px", threshold: 0});
        for (let i = 0; i < heads.length; i++) {
            this.observer.observe(heads[i]);
        }
    }
}
